import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Character } from '@/entities/character/types/character'
import { migrateCharacterKeys } from '../lib/migrate'

/** A character still being created: kept in this browser only, never synced to the cloud. */
export interface CharacterDraft {
  id: string
  character: Character
  /** The wizard step it was on (a step key, e.g. `"ability"`), so Continue reopens it there. */
  stepKey: string
  savedAt: number
}

interface DraftsStore {
  drafts: CharacterDraft[]
  /** Create or replace the draft with this id. */
  upsert: (id: string, character: Character, stepKey: string) => void
  remove: (id: string) => void
}

export function newDraftId(): string {
  return `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

// Deliberately separate from the saved characters: nothing here touches Supabase, so an
// unfinished character can't reach the cloud, a party or a share link.
export const useDraftsStore = create<DraftsStore>()(
  persist(
    (set) => ({
      drafts: [],
      upsert: (id, character, stepKey) =>
        set((s) => {
          const entry: CharacterDraft = { id, character, stepKey, savedAt: Date.now() }
          return { drafts: s.drafts.some((d) => d.id === id) ? s.drafts.map((d) => (d.id === id ? entry : d)) : [...s.drafts, entry] }
        }),
      remove: (id) => set((s) => ({ drafts: s.drafts.filter((d) => d.id !== id) })),
    }),
    {
      name: 'dnd-character-drafts',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ drafts: state.drafts }),
      // Drafts written by older builds get the same legacy-key normalization as saves.
      merge: (persisted, current) => ({
        ...current,
        drafts: ((persisted as { drafts?: CharacterDraft[] } | undefined)?.drafts ?? []).map((d) => ({
          ...d,
          character: migrateCharacterKeys(d.character),
        })),
      }),
    },
  ),
)
